import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, ArrowUpRight, Award, BookOpen, CheckCircle2, ChevronLeft, ChevronRight,
  ClipboardCheck, ClipboardList, Clock, GraduationCap, Link as LinkIcon, Minus, ShieldCheck, TrendingDown,
  TrendingUp, Users,
} from 'lucide-react';
import { api, toApiError } from '@/lib/api';
import {
  priorityStyles, useAnnouncementFeed, type AnnouncementItem,
} from '@/lib/announcementQueries';
import {
  activityDelta, comparisonPhrase, formatMinutes, periodPhrase, useActivityRange,
  type ActivityRange, type RangePreset,
} from '@/lib/activityQueries';
import { ActivityRangePicker } from '@/components/dashboard/ActivityRangePicker';
import { courseArt } from '@/components/course/courseArt';
import { AnnouncementDialog, AnnouncementThumb } from '@/components/announcements/AnnouncementDialog';
import { firstLink, shortenUrl, withoutLink } from '@/lib/linkify';
import { PersonalNotepad } from '@/components/dashboard/PersonalNotepad';
import { NeedsAttentionPanel } from '@/components/team/NeedsAttentionPanel';
import { ActivityLineChart } from '@/components/dashboard/ActivityLineChart';
import { LearningSpark } from '@/components/dashboard/LearningSpark';
import { ManagerQuickActions } from '@/components/dashboard/ManagerQuickActions';
import type { TeamData } from '@/components/dashboard/TeamOverview';
/**
 * `MandatoryCourseStatus`, `YourOwnLearning`, `NeedsAttentionPanel` and
 * `DepartmentCompliance` were imported here until those panels were taken off
 * the dashboard by request. `ComplianceTrend` and the trends `LearningActivity`
 * joined them when the admin dashboard was cut back.
 *
 * `HoursActivity` went the same way and has since come back in place of the
 * activity feed — which is the point of keeping these: restoring one was an
 * import and a line, exactly as this note promised.
 *
 * The components are all still in the tree and still fed by the API — see the
 * note on `AdminDashboard` — and `trends.compliance` still arrives in the
 * payload for the same reason.
 */
import {
  AttentionTasks, QuickActions, compactNumber, type AdminData,
} from '@/components/dashboard/AdminOverview';
import type { TrendsData } from '@/components/dashboard/DashboardTrends';
import { PlatformActivity } from '@/components/dashboard/PlatformActivity';
import { MyCourses } from '@/components/dashboard/TrainerDesk';
import { useTrainerDesk } from '@/lib/trainerQueries';
import { SYSTEM_ROLES } from '@lms/shared';
import type { ScheduleEntry } from '@/components/dashboard/HoursAndSchedule';
import { DailySchedule, HoursActivity } from '@/components/dashboard/HoursAndSchedule';
import { useAuthStore } from '@/stores/auth';
import { useViewModeStore } from '@/stores/viewMode';
import { ErrorState, LoadingState } from '@/components/States';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface CourseSummary {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  durationMinutes: number | null;
  category: { name: string; color: string | null } | null;
  resourceCount: number;
  isMandatory: boolean;
  dueDate: string | null;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  percent: number;
  isOverdue: boolean;
}

interface DashboardData {
  /**
   * Today's calendar, for every role.
   *
   * Outside `trends`, which an administrator alone receives — the Daily
   * schedule panel is on all three dashboards now, and the server scopes the
   * rows per viewer (PRD §12A).
   */
  schedule: ScheduleEntry[];
  learner: {
    stats: {
      assigned: number;
      completed: number;
      inProgress: number;
      notStarted: number;
      overdue: number;
      completionPercent: number;
      assessmentsPassed: number;
      averageScore: number | null;
      certificates: number;
      /**
       * Measured learning time since the first of this month.
       *
       * Month-to-date, not year-to-date: a year's total barely moves, so the
       * tile looked frozen however much somebody used the system.
       */
      hoursThisMonth: number;
      /** The same figure unrounded, so small amounts are still visible. */
      minutesThisMonth: number;
      /** Against the same days of last month; null when there is too little
          of last month to divide by. See the "This month" tile. */
      minutesChangePercent: number | null;
    };
    /** Mandatory courses only — what compliance is actually measured on. */
    mandatory: {
      total: number;
      completed: number;
      outstanding: CourseSummary[];
      /** Outstanding courses beyond the ones listed. */
      hidden: number;
    };
    /** Twelve months of learning time, newest last. */
    /**
     * This month, day by day — what the chart opens on. Sent inline so the
     * first paint costs no extra request; the card refetches from
     * `/dashboard/activity` whenever the range picker moves.
     */
    activity: ActivityRange & {
      /** When anything was last recorded, so an empty chart can say why. */
      lastActiveOn: string | null;
    };
    deadlines: CourseSummary[];
    /** Deadlines and calendar events on one timeline. */
    upcoming: {
      id: string;
      title: string;
      at: string;
      kind: 'Deadline' | 'Due' | 'Session' | 'Event';
      detail: string | null;
      link: string;
      isOverdue: boolean;
    }[];
    /** Every milestone, earned first. `earnedAt` is null on a locked one. */
    badges: { name: string; icon: string; description: string; earnedAt: string | null }[];
    /** The denominator on "3 of 9". */
    totalBadges: number;
  };
  /**
   * Managers and admins only; null for an ordinary learner.
   *
   * Declared beside the component that renders it, so the page and the My
   * Departments page cannot drift into two shapes for one payload.
   */
  team: TeamData | null;
  /**
   * Organisation-wide KPIs. Present only for an organisation-scoped caller —
   * the API decides, so this page never repeats the rule about who it is for.
   */
  admin: AdminData | null;
  /** Trends and the activity feed. Organisation-scoped callers only. */
  trends: TrendsData | null;
}

/**
 * How many of the people behind the dashboard names before deferring.
 *
 * A dashboard is read at a glance. Eight rows of 0% stacked above everything
 * else on the page is a list nobody finishes — four says whether there is a
 * problem and who is worst, and My Departments is where the work is actually
 * done.
 */
const DASHBOARD_ATTENTION_SHOWN = 4;


export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const viewMode = useViewModeStore((s) => s.mode);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const { data: result } = await api.get<DashboardData>('/dashboard');
      return result;
    },
  });

  if (!user) return null;
  if (isLoading) return <LoadingState label="Loading your dashboard…" />;
  if (isError) return <ErrorState message={toApiError(error).message} onRetry={() => void refetch()} />;
  if (!data) return null;

  const { learner, team, admin, trends, schedule } = data;

  const { mandatory } = learner;
  const outstandingMandatory = mandatory.total - mandatory.completed;

  /**
   * Days until the soonest mandatory deadline, or null when none has a date.
   *
   * `outstanding` arrives sorted by due date with undated courses last, so the
   * first dated one is the nearest.
   */
  const nearestDue = (() => {
    const next = mandatory.outstanding.find((c) => c.dueDate);
    if (!next?.dueDate) return null;
    return Math.ceil((new Date(next.dueDate).getTime() - Date.now()) / 86_400_000);
  })();

  /**
   * ── AN ADMINISTRATOR GETS A DIFFERENT PAGE ─────────────────────────────
   *
   * Not a learner's dashboard with panels appended. The question an
   * administrator opens with is "how is the organisation doing, and what needs
   * me" — so that is what the top of the page answers, and their own training
   * becomes one line rather than the hero.
   *
   * Everyone else keeps exactly the page they had. The branch is on `admin`
   * being present, which the API sends only to an organisation-scoped caller,
   * so a Manager's dashboard is untouched by all of this.
   *
   * ── And on which of their two jobs they are doing ──────────────────────
   *
   * An administrator in EMPLOYEE view falls through to the learner page below,
   * with their own courses and their own deadlines. That is the point of the
   * switch beside their name: swapping the menu alone would answer "what does
   * my team see" with a list of links rather than the screen itself.
   *
   * The payload is unchanged either way — the API does not know or care which
   * view is selected, so switching costs no request and reveals nothing new.
   */
  if (admin && viewMode === 'admin') {
    return (
      <AdminDashboard
        admin={admin}
        trends={trends}
        schedule={schedule}
        team={team}
        firstName={user.firstName}
        permissions={user.permissions}
        roleSlugs={user.roles.map((role) => role.slug)}
      />
    );
  }

  /**
   * Whether ANY tile on the learner's strip has something under its label.
   *
   * Only one of the four can — the change badge on "This month" — and it is
   * absent whenever last month was too thin to compare against, which is most
   * accounts most of the time. The strip decides once and tells all four
   * tiles, so they either all keep room for that line or none of them does;
   * a tile cannot see its neighbours to work this out for itself.
   */
  const showsLearningDelta = learner.stats.minutesChangePercent !== null;

  return (
    <>
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="mb-5 overflow-hidden rounded-2xl border border-brand-100 bg-brand-50/70 dark:border-brand-800 dark:bg-brand-900/60">
        {/*
          Two columns: everything textual on the left, the illustration hard
          against the right edge. `items-end` on the row plus `-mb-5` on the
          image lets the desk run into the panel's lower edge instead of
          floating clear of it.

          ── Why the height is split between two numbers ────────────────────
          The banner was asked to lose about half a centimetre — roughly 20px
          — and it could not all come from the padding, because the ILLUSTRATION
          is what sets this row's height, not the text beside it. At 190px
          against a text column of about 150px, trimming the padding alone
          would have moved the text and left the row exactly as tall.

          So: 12px off the padding (`py-5` → `py-3.5`) and 8px off the artwork
          (190 → 182). The same pair of changes is on the admin hero below —
          they are the same banner with different figures in it, and one
          shrinking without the other would be the more obvious bug.
        */}
        <div className="flex items-end gap-6 px-6 py-3.5 sm:px-7">
          <div className="min-w-0 flex-1">
            <h1 className="text-[22px] font-semibold leading-tight tracking-tight">
              Hello, {user.firstName}
            </h1>
            <p className="mt-1 text-sm text-foreground/70">{tagline(user.permissions)}</p>

            {/* The one thing worth saying before anything else. */}
            <p className="mt-2 text-sm text-muted-foreground">
              {outstandingMandatory === 0 ? (
                mandatory.total > 0 ? (
                  <>All mandatory training complete.</>
                ) : (
                  <>Nothing mandatory assigned right now.</>
                )
              ) : (
                <>
                  You have{' '}
                  <strong className="font-semibold text-foreground">
                    {outstandingMandatory} mandatory course{outstandingMandatory === 1 ? '' : 's'}
                  </strong>{' '}
                  outstanding.
                  {nearestDue !== null && (
                    <> Nearest {nearestDue < 0 ? 'is overdue' : dueInWords(nearestDue)}.</>
                  )}
                </>
              )}
            </p>

            {/*
              The four figures, beneath the message and inside the same panel.

              One row of four rather than the old 2×2 in the corner: below the
              text there is the full width of the column to use, so each figure
              gets room instead of being squeezed against the illustration.
            */}
            {/*
              ── THE LINES ARE THE TILES', NOT THE CONTAINER'S ───────────────

              This was `divide-x divide-y … sm:divide-y-0`, which is wrong at
              any width where the grid wraps. Tailwind's `divide-*` is
              `> :not([hidden]) ~ :not([hidden])` — EVERY sibling after the
              first, with no idea where the grid put it. On the two-column
              phone layout that drew a vertical line down the left of the
              third tile, which starts a row, and a horizontal line above the
              second, which does not.

              Each tile now carries its own top and left border, and the grid
              is pulled up and left by a pixel so the outermost of those land
              under the container's own border and are clipped away. What
              survives is exactly the interior lines — at two columns, at
              four, and at any count either strip grows to.
            */}
            {/*
              ── 9.6rem A TILE, THE SAME AS THE ADMINISTRATOR'S ─────────────

              This was `34rem`, which over four columns is 8.5rem a tile — an
              18px narrower box than the five-column strip on the other
              dashboard, which gets 9.6rem from its 48rem. Same component,
              same labels, visibly smaller boxes, and the squeeze landed on
              exactly the two longest labels this strip has: `COURSES DONE`
              and `CERTIFICATES` had 108px of content width to sit in against
              the other strip's 126px.

              38.4rem is four of the administrator's tiles, so a box is now
              the same size on whichever dashboard you land on.
            */}
            <div className="mt-5 max-w-[38.4rem] overflow-hidden rounded-xl border border-brand-200/60 bg-white/60 dark:border-white/10 dark:bg-white/[0.04]">
              <div className="-ml-px -mt-px grid grid-cols-2 sm:grid-cols-4">
              {/* Each figure leads to its own screen — see `HeroStat`. */}
              <HeroStat
                reserveQualifier={showsLearningDelta}
                icon={GraduationCap}
                value={learner.stats.completed}
                label="Courses done"
                to={linkIf(user.permissions, 'learning.view_own', '/my-courses')}
              />
              <HeroStat
                reserveQualifier={showsLearningDelta}
                icon={Clock}
                value={formatLearningTime(learner.stats.minutesThisMonth)}
                label="This month"
                /*
                  The only tile on this strip that may carry one.

                  A change badge belongs on a FLOW — something accumulated
                  inside a period — and this is the only one here. Its three
                  neighbours count courses done, assessments passed and
                  certificates held: stocks that cannot go down, so a delta on
                  any of them would be permanently green and permanently
                  uninformative.

                  Null when last month is too thin to divide by, and the tile
                  then simply shows the time. See `periodChange`.
                */
                delta={learner.stats.minutesChangePercent}
                /*
                  The one tile with no page behind it: minutes of learning are
                  drawn by the Learning activity chart on this very page and
                  nowhere else. Rather than invent a destination or leave the
                  tile dead, it scrolls to the chart it summarises.
                */
                to="#learning-activity"
              />
              <HeroStat
                reserveQualifier={showsLearningDelta}
                icon={ClipboardCheck}
                value={learner.stats.assessmentsPassed}
                label="Assessments"
                to={linkIf(user.permissions, 'assessment.take', '/my-assessments')}
              />
              <HeroStat
                reserveQualifier={showsLearningDelta}
                icon={Award}
                value={learner.stats.certificates}
                label="Certificates"
                /* No permission guards `/my-certificates` — your own
                   certificates are yours to see by definition. */
                to="/my-certificates"
              />
            </div>
            </div>
          </div>

          {/* Cut out to transparency, so it sits on the band rather than in a
              box of its own. */}
          <img
            src="/hero-art.png"
            alt=""
            className="-mb-5 hidden h-[182px] w-auto shrink-0 object-contain lg:block"
          />
        </div>
      </section>

      {/*
        Sixty-five / thirty-five, matching the admin dashboard's pair above.

        This was three equal columns with the main one spanning two — which is
        a third for announcements, not far off, but it made the ratio a
        consequence of the column count rather than a decision. Stated as the
        ratio it is, the two dashboards now agree, and changing it is one
        number instead of two classes that have to keep each other honest.
      */}
      <div className="grid gap-6 lg:grid-cols-[13fr_7fr]">
        <div className="min-w-0 space-y-6">
          {/*
            ── Today's Learning Spark ───────────────────────────────────

            Above the mandatory list on purpose: it is the one thing here
            that is only worth doing today, and it takes half a minute. The
            courses below will still be there afterwards.

            Fetches its own data — including both streaks, since which one it
            shows depends on whether there is a question today.
          */}
          <LearningSpark />

          {/*
            ── A manager's management work, on the page they land on ─────

            Directly under the Spark and ABOVE their own mandatory learning,
            by request. A Manager opens this page to run their team, so the
            four screens they run it from come before their own coursework —
            which is still right here, one panel down, rather than off in a
            section they have to go looking for.

            `!admin` is the gate, not a permission.

            An administrator holds all four of these permissions and would
            otherwise get this panel in EMPLOYEE VIEW — which exists to answer
            "what does my team see", and answers it wrongly the moment it
            carries a management panel no employee has. The admin dashboard
            has its own Quick actions; this is the other one. An Employee
            holds none of the four, so for them it draws nothing and the
            position costs nothing.
          */}
          {!admin && (
            <ManagerQuickActions
              permissions={user.permissions}
              /* Counted the same way their own screens count — see
                 `dashboard.service.ts`, where both compose scope AND
                 authorship so the badge and the queue agree. */
              pending={{
                evaluations: team?.stats.pendingEvaluations ?? 0,
                questionReports: team?.stats.openQuestionReports ?? 0,
              }}
            />
          )}

          {/* ── Mandatory learning ────────────────────────────────────── */}
          {mandatory.total > 0 && (
            <Card className="overflow-hidden">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b px-4 py-3">
                <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
                <h2 className="text-sm font-semibold">Mandatory learning</h2>
                <span className="text-xs text-muted-foreground">
                  Required — due dates enforced
                </span>
                <span className="ml-auto shrink-0 rounded-full bg-clay-50 px-2.5 py-0.5 text-[11px] font-medium text-clay-700">
                  <span className="font-mono">
                    {mandatory.completed}/{mandatory.total}
                  </span>{' '}
                  complete
                </span>
              </div>

              {mandatory.outstanding.length === 0 ? (
                <div className="flex flex-col items-center px-6 py-8 text-center">
                  <CheckCircle2 className="mb-2 h-7 w-7 text-success" aria-hidden />
                  <p className="text-sm font-medium">All mandatory training complete</p>
                </div>
              ) : (
                <ul className="divide-y">
                  {mandatory.outstanding.map((course) => (
                    /*
                      One row per course, on one line.

                      This used to wrap: `flex-wrap` with a `basis-56` middle
                      column meant the due chip and the button dropped onto a
                      second line the moment the title was long, so each row was
                      two rows tall and the panel ran down the page. Nothing here
                      needs to wrap — the title truncates and the bar is fixed —
                      so the row stays one line and the list stays scannable.
                    */
                    <li key={course.id} className="flex items-center gap-3 px-4 py-2.5">
                      <CourseTile course={course} size="sm" />

                      <div className="min-w-0 flex-1">
                        {/*
                          `leading-[18px]` rather than `leading-tight`, so the
                          block's height is a whole number.

                          `leading-tight` on 14px text is 17.5px, which made
                          this column 37.5px against a 36px button — and a
                          half-pixel of leftover centred as a quarter-pixel
                          above and below, which is precisely the amount that
                          makes a button look a hair off without anybody being
                          able to say why. 18 + 6 + 14 = 38, so the button now
                          centres on a clean 1px each side.
                        */}
                        <Link
                          to={`/courses/${course.id}`}
                          className="block truncate text-sm font-semibold leading-[18px] hover:underline"
                        >
                          {course.title}
                        </Link>
                        {/*
                          ── The progress line stops at 80% ──────────────────

                          The remaining fifth of the column is deliberately
                          empty: it is the clear space between "in progress"
                          and the Resume button, and it is a PROPORTION rather
                          than a fixed margin so it holds at every width — a
                          24px gap that looks generous on a phone is a hairline
                          against a wide panel.

                          Only this line is capped. The title above still runs
                          the full width, because a truncated course name is a
                          real cost and whitespace beside it is not.
                        */}
                        <div className="mt-1.5 flex w-4/5 items-center gap-2.5">
                          {/*
                            The bar takes whatever width the row has left.

                            It was pinned at 150px, which on a wide panel left
                            it a stub against half a column of empty space —
                            and a progress bar is read by its PROPORTION, so
                            the narrower it is drawn the less it says. 20% of
                            150px is thirty pixels of colour; across the column
                            it is a length somebody can actually judge.

                            `min-w-0` lets it give way before the figures
                            beside it do, and the floor stops it collapsing to
                            a sliver on a phone.
                          */}
                          <div className="h-1.5 min-w-[3rem] flex-1 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-brand-800 transition-[width] duration-500"
                              style={{ width: `${course.percent}%` }}
                            />
                          </div>
                          {/*
                            The figure and the state, as ONE group rather than
                            two siblings of the bar.

                            Grouped because the spacing inside them and the
                            spacing between them and the bar are different
                            jobs: 4px holds "20%" to its own "· in progress",
                            10px holds the pair clear of the track. A single
                            `gap-2` on all three made the reading order look
                            like three equal items instead of a bar and its
                            caption.

                            `shrink-0`, because the bar is the flexible one —
                            two things competing for the slack is how a figure
                            ends up truncated to "2…".
                          */}
                          {/*
                            `leading-[14px]` is the vertical half of this fix.

                            `text-[11px]` sets a font size and nothing else, so
                            this line inherited the body's 21px line-height —
                            making the two-line block about 44px against a 36px
                            button. Centred in the row, the button then sat
                            visibly high, straddling the title and the bar
                            instead of squaring up with them. At 14px the block
                            comes to ~37px and the two finally agree; 14 rather
                            than `leading-none` so "progress" keeps its
                            descenders.
                          */}
                          <div className="flex shrink-0 items-center gap-1 font-mono text-[11px] leading-[14px] text-muted-foreground">
                            {/*
                              Fixed width, right-aligned, so the percentages
                              stack into a column down the list and everything
                              after them starts at the same x. Left to size
                              itself, "100%" is a character wider than "20%"
                              and shunts the state text along by one — which is
                              exactly the ragged edge the mono font was chosen
                              to avoid.
                            */}
                            <span className="w-8 text-right">{Math.round(course.percent)}%</span>
                            {/*
                              "15m left" needs the author's duration estimate,
                              and plenty of courses have none. Rather than leave
                              the line as a bare percentage, say where the
                              course stands — which is the thing the number was
                              standing in for anyway.
                            */}
                            <span className="whitespace-nowrap">
                              {course.durationMinutes != null
                                ? `· ${remainingLabel(course.durationMinutes, course.percent)}`
                                : course.percent > 0
                                  ? '· in progress'
                                  : '· not started'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* The due chip is the first thing to go on a narrow
                          column — the button is the action, and the date is
                          repeated on the course page. */}
                      {course.dueDate && (
                        <span
                          className={cn(
                            'hidden shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium sm:inline',
                            course.isOverdue
                              ? 'bg-destructive/10 text-destructive'
                              : 'bg-muted text-muted-foreground',
                          )}
                        >
                          {course.isOverdue ? 'Overdue' : `Due ${shortDate(course.dueDate)}`}
                        </span>
                      )}

                      {/*
                        The clear space to the left of this button is the 20%
                        the progress line gives up — see the note on that line.
                        No margin here: two separate rules trying to own the
                        same gap is how it ends up neither 20% nor 16px.

                        `min-w` because the label is "Resume" on a started
                        course and "Start" on an untouched one: sized to its
                        text, the buttons down the list would have had ragged
                        left edges for no reason a reader could see.

                        `h-9` is what `size="sm"` already gives; stated because
                        the vertical alignment above is arithmetic against this
                        number, and a variant change that silently altered it
                        should be visible here.
                      */}
                      <Button
                        asChild
                        size="sm"
                        className="h-9 min-w-[5.25rem] shrink-0 px-4 text-[13px]"
                      >
                        <Link to={`/courses/${course.id}`}>
                          {course.percent > 0 ? 'Resume' : 'Start'}
                        </Link>
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              {/* The list is capped. Saying so is not optional on a compliance
                  panel — an unlisted requirement is one nobody knows to do. */}
              {mandatory.hidden > 0 && (
                <Link
                  to="/my-courses"
                  className="block border-t px-4 py-2.5 text-center text-[13px] font-medium text-brand-700 transition-colors hover:bg-muted/40"
                >
                  {mandatory.hidden} more outstanding
                </Link>
              )}
            </Card>
          )}

          {/* Achievements — one compact strip, earned first, locked dimmed. */}
          <AchievementStrip badges={learner.badges} totalBadges={learner.totalBadges} />

          {/* A private scratchpad, below the achievements in the same column. */}
          <PersonalNotepad />
        </div>

        {/*
          ── ANNOUNCEMENTS, DEADLINES, AND A MANAGER'S PEOPLE ────────────────

          A flex column rather than a plain block, so the last panel in it can
          be told to take the room that is left. Without that, this side
          finished wherever its content happened to and the notepad opposite
          ran on below it, leaving a rectangle of white beside the notes.
        */}
        <div className="flex min-w-0 flex-col">
          {/* ── Announcements ─────────────────────────────────────────── */}
          <DashboardAnnouncements />

          {/*
            ── TODAY'S SESSIONS, THE SAME PANEL AN ADMIN SEES ───────────────

            This was "Upcoming", a card of its own that merged course deadlines
            with today's events. It is the Daily schedule component now, by
            request, so that a live session can be joined from the dashboard
            instead of being a row that links to the calendar to find the link
            again. One component across all three roles, so the three cannot
            drift.

            WHAT WENT WITH IT: the deadline half. `Upcoming` was the only place
            a learner's own due and overdue courses appeared on this page — the
            calendar screen still lists them, and `learner.deadlines` is still
            in the payload, but nothing here renders it. Worth putting back as
            its own panel if the dashboard should still answer "what is due".

            The rows are scoped by the server per viewer: an employee sees
            organisation events and their own department's, never another
            department's (PRD §12A).
          */}
          <DailySchedule schedule={schedule} />

          {/*
            ── WHO ON MY TEAM IS BEHIND ────────────────────────────────────

            Managers only — `team` is null for everybody else, so a plain
            employee's dashboard is untouched by this. It is the same panel My
            Departments draws, so the two can never disagree about who needs
            chasing.

            ── SIZED TO ITS FOUR ROWS, NOT TO THE COLUMN ─────────────────

            This was `flex-1` with `fill`, so it stretched to finish level with
            the notepad opposite and scrolled inside itself. Reversed by
            request: with the list capped at four, stretching bought nothing
            but a panel two thirds empty — four names, then a field of white,
            then the link.

            The cap is what makes the stretch pointless. `fill` existed for
            this one call site — My Departments never used it — so it went with
            the stretch rather than staying as a prop nothing sets. The panel
            now takes the room its rows need and the column ends where it ends.
          */}
          {team && (
            <div className="mt-6">
              <NeedsAttentionPanel
                members={team.needsAttention}
                /* Four is enough to know whether there is a problem and who
                   is at the sharp end of it. The rest are one click away, and
                   the header still reports the true total. */
                limit={DASHBOARD_ATTENTION_SHOWN}
                /* In the header rather than under the rows, and short enough
                   to live there — the count is already beside the title, so
                   repeating it in the link said the same number twice. */
                action={
                  team.needsAttention.length > 0 ? (
                    <Link
                      to="/my-departments"
                      className="text-xs font-medium text-brand-700 transition-colors hover:underline"
                    >
                      See all
                    </Link>
                  ) : undefined
                }
              />
            </div>
          )}

          {/* ── Learning activity ─────────────────────────────────────── */}
          <LearningActivity activity={learner.activity} />

        </div>
      </div>

      {/*
        No organisation overview below this point, and none is missing.

        It used to sit here for an Admin, under their own learning. They now
        get `AdminDashboard` instead — which opens with it — and this branch is
        only reached by somebody who is NOT organisation-scoped, so the panel
        would have had nothing to draw. A Manager's version has had its own
        page for a while, one click below Dashboard.
      */}
    </>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE ADMINISTRATOR'S DASHBOARD
 *
 * Four bands, in the order the questions get asked: how are we doing, what
 * needs me, which way is it moving, and what just happened.
 *
 * `TeamStatCards` is deliberately not rendered here. It reports employees,
 * completion, completed and awaiting-evaluation, and the KPI row above already
 * carries the organisation-wide version of each — two rows of tiles disagreeing
 * slightly (one counts all courses, the other only mandatory) is worse than one
 * row that is clear about what it counts.
 *
 * ── What used to sit below, and where it went ──────────────────────────────
 *
 * The two activity feeds — Recent activity and Manager activity — MOVED to the
 * Audit Log screen, data and all. A list of individual edits answers "what has
 * been happening", which is that screen's question; this one answers "where do
 * we stand". The dashboard payload no longer carries them.
 *
 * The department compliance table, the per-course mandatory status, the
 * people-to-chase list and the administrator's own learning were removed by
 * request. None of THAT code was deleted: the components still exist, the API
 * still returns `admin.departments` and `admin.mandatoryCourses`, and each
 * panel is a single line to put back.
 *
 * Which does mean the server computes two rollups nobody currently reads. That
 * is a deliberate, stated cost — not an oversight — kept because this layout
 * has been iterated on repeatedly and throwing the queries away would make
 * restoring a panel a much larger job than removing it was.
 * ═══════════════════════════════════════════════════════════════════════════
 */
function AdminDashboard({
  admin,
  trends,
  schedule,
  team,
  firstName,
  permissions,
  roleSlugs,
}: {
  admin: AdminData;
  trends: TrendsData | null;
  /** Today's calendar. Top level rather than inside `trends`, because every
   *  role gets it — see `DashboardData`. */
  schedule: ScheduleEntry[];
  /** Still needed: the attention band reads the marking queue from it. */
  team: TeamData | null;
  firstName: string;
  permissions: string[];
  /**
   * Which roles the reader holds.
   *
   * Permissions decide almost everything on this screen, but not the Quick
   * actions panel: a Trainer and an Admin can hold the same permission and
   * still want different tiles offered. See `actionsFor`.
   */
  roleSlugs: string[];
}) {
  /**
   * ── A TRAINER'S PANELS INSTEAD OF THE ORGANISATION CHART ────────────────
   *
   * Platform activity asks "is the organisation learning more than it was",
   * which a trainer can read and do nothing about. In its place go their own
   * courses, their next session and their marking queue.
   *
   * Read as "a trainer and NOT an administrator", which is the same rule
   * `hiddenForRoles` uses in the navigation: somebody holding both roles keeps
   * the admin view, because the point is to give a trainer something useful
   * rather than to take the chart off an administrator who also trains.
   */
  const isTrainerOnly =
    roleSlugs.includes(SYSTEM_ROLES.TRAINER) && !roleSlugs.includes(SYSTEM_ROLES.ADMIN);

  /* Fetches for nobody else — see `enabled` on the hook. */
  const { data: desk } = useTrainerDesk(isTrainerOnly);

  /**
   * ── WHICH TILES, AND WHAT IS IN THEM, ARE TWO QUESTIONS ─────────────────
   *
   * `isTrainerOnly` decides WHICH — Assessments or Certificates, own
   * compliance or the organisation's — and it is known on the first render.
   * `desk` decides what the trainer's tiles SAY, and arrives a moment later.
   *
   * They were one value, `desk?.kpis ?? null`, and null meant "show the
   * organisation's". So a trainer's dashboard opened on organisation-wide
   * compliance and a Certificates tile they cannot act on, then swapped to
   * their own figures once the request landed — a visible flash of somebody
   * else's numbers under their own heading, and the opposite of what the
   * comment here used to claim.
   *
   * Split, the layout is settled immediately and only the figures wait.
   */
  const showOwnKpis = isTrainerOnly;
  const trainerKpis = desk?.kpis ?? null;

  const { compliance, activeLearners, courses, certificates, learningHours } = admin;

  return (
    <>
      {/*
        ── Hero ──────────────────────────────────────────────────────────

        The SAME banner an employee gets — same band, same illustration, same
        `HeroStat` tiles — carrying organisation figures instead of personal
        ones. Written here rather than as a separate admin component precisely
        so it cannot drift into a lookalike: change the tile and both move.
      */}
      <section className="mb-5 overflow-hidden rounded-2xl border border-brand-100 bg-brand-50/70 dark:border-brand-800 dark:bg-brand-900/60">
        <div className="flex items-end gap-6 px-6 py-3.5 sm:px-7">
          <div className="min-w-0 flex-1">
            <h1 className="text-[22px] font-semibold leading-tight tracking-tight">
              Hello, {firstName}
            </h1>
            <p className="mt-1 text-sm text-foreground/70">{tagline(permissions)}</p>

            {/*
              The compliance sentence that stood here has moved into "What
              needs your attention" as a task.

              It said the right thing in the wrong place: a statement of a
              problem, in a banner, with nowhere to click. As a task it carries
              the same figure plus the departments behind it and a way through
              to the report — and it disappears on its own when the target is
              met, which a hero sentence never would.
            */}

            {/*
              One short line per tile, and the full definition on hover.

              Each of these had a plausible WRONG reading — "Active" could be
              users, courses or learners; "Courses" could be all of them or
              only the live ones — so the sub-line exists to settle that, and
              nothing more. Spelling out population, period AND target on every
              tile turned five columns into a paragraph in five pieces.

              The target lives in the sentence above this row, where it is said
              once instead of five times.
            */}
            {/* Same construction as the learner strip above, and for the same
                reason — see the note there. Five columns rather than four. */}
            <div className="mt-5 max-w-[48rem] overflow-hidden rounded-xl border border-brand-200/60 bg-white/60 dark:border-white/10 dark:bg-white/[0.04]">
              <div className="-ml-px -mt-px grid grid-cols-2 sm:grid-cols-5">
              {/*
                Each tile goes to the screen it summarises, and only where the
                reader may open it — `linkIf` returns undefined otherwise, and
                the tile falls back to a plain figure. Linking regardless would
                send somebody to the route guard and bounce them back here,
                which is worse than not offering the link.
              */}
              {/*
                ── COMPLIANCE MEANS SOMETHING DIFFERENT TO A TRAINER ────────

                An administrator's tile is the organisation's mandatory
                training. A trainer's is their OWN work: of the people they
                handed a course or an exam to, how many have finished all of
                it. Same question, a scope they can actually act on — the
                organisation's figure moves on decisions that were never
                theirs.

                The two are computed separately on the server, and the one not
                being shown is never fetched.
              */}
              {showOwnKpis ? (
                <HeroStat
                  icon={ShieldCheck}
                  value={trainerKpis ? `${Math.round(trainerKpis.compliance.percent)}%` : '—'}
                  label="Compliance"
                  /* Their own reach, where the shortfall has names against
                     it — the department report is about somebody else's. */
                  to={linkIf(permissions, 'report.view', '/admin/reports?type=course')}
                  sub={
                    !trainerKpis
                      ? 'loading'
                      : trainerKpis.compliance.peopleTotal === 0
                        ? 'nobody assigned yet'
                        : `${trainerKpis.compliance.peopleCompliant} of ${trainerKpis.compliance.peopleTotal} people`
                  }
                  detail={
                    !trainerKpis
                      ? 'Working out how much of your training has been completed.'
                      : trainerKpis.compliance.peopleTotal === 0
                      ? 'Nobody holds a course or assessment you wrote yet.'
                      : `${trainerKpis.compliance.peopleCompliant} of ${trainerKpis.compliance.peopleTotal} people have finished everything you assigned them — ${trainerKpis.compliance.completed} of ${trainerKpis.compliance.total} items done. Counts your published courses and your assessments, and an exam counts once it is passed.`
                  }
                />
              ) : (
                <HeroStat
                  icon={ShieldCheck}
                  value={`${Math.round(compliance.percent)}%`}
                  label="Compliance"
                  /* The department report — compliance is a per-department
                     number before it is an organisation one, and that table is
                     where the shortfall has names against it. */
                  to={linkIf(permissions, 'report.view', '/admin/reports?type=department')}
                  /* People, not assignments — somebody two courses short of
                     five is not 60% compliant, they are not compliant. */
                  sub={
                    compliance.peopleTotal === 0
                      ? 'none assigned'
                      : `${compliance.peopleCompliant} of ${compliance.peopleTotal} people`
                  }
                  detail={
                    compliance.peopleTotal === 0
                      ? 'Nobody currently holds mandatory training.'
                      : `${compliance.peopleCompliant} of ${compliance.peopleTotal} people have completed every mandatory course they hold. Target ${compliance.target}%. ${compliance.completed} of ${compliance.total} mandatory assignments are done.`
                  }
                />
              )}
              <HeroStat
                icon={Users}
                value={compactNumber(activeLearners.active)}
                label="Active learners"
                sub="this month"
                /**
                 * The DIFFERENCE, not the ratio.
                 *
                 * `changePercent` is still on the payload and still guarded —
                 * it is simply not what this tile shows any more. Subtraction
                 * needs no guard of its own: it cannot divide by a near-empty
                 * previous month, so it says something true on a young
                 * deployment where a percentage would have to say nothing.
                 */
                delta={activeLearners.active - activeLearners.priorActive}
                deltaUnit="count"
                /**
                 * To the DEPARTMENT report, not the user list.
                 *
                 * The flat list of everybody answered a different question
                 * from the one the tile asks: this figure is "how many of my
                 * people are actually using it", and the useful next step is
                 * which departments they are in. That report carries an
                 * "Active" column on the same calendar month, totalling
                 * exactly this number, and each row drills through to the
                 * people in it.
                 */
                to={linkIf(permissions, 'report.view', '/admin/reports?type=department')}
                detail={`${activeLearners.active} of ${activeLearners.headcount} active staff have recorded measured learning time in ${activeLearners.windowLabel}. Compared with the same stretch of ${activeLearners.priorLabel}, when ${activeLearners.priorActive} did.`}
              />
              <HeroStat
                icon={BookOpen}
                value={showOwnKpis ? (trainerKpis?.coursesCreated ?? '—') : courses.published}
                label="Courses"
                /* "written" for a trainer, because the figure counts drafts
                   and archived ones too — it is their output, not the
                   catalogue. Saying "published" over a count that includes
                   drafts would be wrong by one word. */
                sub={showOwnKpis ? 'you wrote' : 'published'}
                to={linkIf(permissions, 'course.view', '/admin/courses')}
                detail={
                  showOwnKpis
                    ? `${trainerKpis?.coursesCreated ?? 0} courses written by you, ${trainerKpis?.published ?? 0} of them published.`
                    : `${courses.published} published courses, ${courses.draft} in draft and ${courses.archived} archived.`
                }
              />
              {/*
                Off a trainer's row entirely, by request.

                Issuing is Admin-only — `certificate.issue` is withheld from
                every other role on all three paths that reach a certificate
                — so this reported a number a trainer could neither move nor
                act on, in the width of a tile that could say something they
                could.
              */}
              {showOwnKpis && (
                <HeroStat
                  icon={ClipboardList}
                  value={trainerKpis?.assessmentsCreated ?? '—'}
                  label="Assessments"
                  sub="you wrote"
                  to={linkIf(permissions, 'assessment.view', '/admin/assessments')}
                  detail={`${trainerKpis?.assessmentsCreated ?? 0} assessments written by you, ${trainerKpis?.assessmentsPublished ?? 0} of them published.`}
                />
              )}

              {!showOwnKpis && (
                <HeroStat
                  icon={Award}
                  value={compactNumber(certificates.issued)}
                  label="Certificates"
                  sub="all time"
                  to={linkIf(permissions, 'certificate.view', '/admin/certificates')}
                  /* Certificates do not expire in this system, so "issued" and
                     "currently valid" are the same number — said plainly here
                     rather than left for somebody to wonder about. */
                  detail={`${certificates.issued} certificates held by staff who still work here, issued at any time — ${certificates.thisMonth} of them this month. Certificates do not expire, so this is also the number currently valid.`}
                />
              )}
              <HeroStat
                icon={Clock}
                value={`${compactNumber(learningHours.hours)}h`}
                label="Learning hours"
                sub="this month"
                delta={learningHours.changePercent}
                /* The employee report, where the hours are broken down by the
                   people who spent them. */
                to={linkIf(permissions, 'report.view', '/admin/reports?type=employee')}
                detail={`${learningHours.hours} hours of measured learning across all ${activeLearners.headcount} active staff in ${learningHours.windowLabel}, against ${learningHours.priorHours} in the same stretch of ${learningHours.priorLabel}.`}
              />
            </div>
            </div>
          </div>

          <img
            src="/hero-art.png"
            alt=""
            className="-mb-5 hidden h-[182px] w-auto shrink-0 object-contain lg:block"
          />
        </div>
      </section>

      {/*
        ── The action band ──────────────────────────────────────────────────

        Directly under the figures, because the order of this page is: how are
        we doing, then what do I do about it, then where is it going wrong.

        Full width, and carrying more weight than it looks: the Content section
        is off the Admin's sidebar entirely now, so for six of those seven
        screens this band is the way in. Losing a card here loses the route.
      */}
      {/*
        Sixty-five / thirty-five across, and the same height down.

        The tasks need the width: a bar, a badge and department chips, where
        Quick actions is four short labels that only get emptier the wider
        they are. Equal halves left the right-hand card mostly whitespace and
        seventy / thirty squeezed its labels, so this sits between them.

        `items-stretch` is the grid's own default and is stated anyway,
        because the two cards being the same height is the thing that was
        actually wanted and it should not read as an accident of the layout.
      */}
      <div className="mb-6 grid items-stretch gap-6 lg:grid-cols-[13fr_7fr]">
        <AttentionTasks
          admin={admin}
          permissions={permissions}
          pendingEvaluations={team?.stats.pendingEvaluations ?? 0}
          /**
           * A trainer reads this row about their own content; null leaves the
           * task exactly as it was for everybody else.
           *
           * The empty shape while the desk is still loading, NOT null — null
           * means "use the organisation's", so a trainer would have opened on
           * "1 of 64 people complete" for training they never assigned and
           * watched it swap. `peopleTotal: 0` is "nothing known yet", and the
           * builder pushes no task at all for it, so the row simply appears
           * once there is something true to put in it.
           */
          ownCompliance={
            showOwnKpis
              ? (trainerKpis?.compliance ?? { percent: 0, peopleCompliant: 0, peopleTotal: 0 })
              : null
          }
        />
        <QuickActions permissions={permissions} roleSlugs={roleSlugs} />
      </div>

      {/*
        ── Direction, then detail ───────────────────────────────────────────

        The two trends sit above the department table on purpose: "are we
        getting better" is the question an administrator asks before "who is
        worst", and answering them the other way round turns every visit into
        a hunt for somebody to chase.
      */}
      {trends && (
        /*
          ── Two thirds to the chart ──────────────────────────────────────

          Four half-width panels competed for the same glance and none of
          them won it. What is left is the one question worth a chart —
          whether completions are keeping up with enrolments — and the feed
          that answers "what changed", which is a list and reads fine narrow.
        */
        <div className="mb-6 grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {isTrainerOnly ? (
              /* Their own work, where the organisation chart sits for an
                 administrator. Rendered only once the desk has loaded —
                 an empty table flashing into a populated one is worse than
                 the panel arriving a moment late. */
              desk && <MyCourses courses={desk.courses} />
            ) : (
              <PlatformActivity usage={trends.usage} />
            )}

            {/*
              ── THE ADMINISTRATOR'S OWN SCRATCHPAD ──────────────────────────

              Under the chart rather than beside it. Shrinking Platform
              activity to a sixth of the page left this column ending well
              above the one opposite, and a rectangle of white beside two
              working panels reads as something that failed to load.

              The same component the employee dashboard carries, not a second
              one: notes are notes, they are private to the person either way,
              and an administrator keeping a reminder about a department is
              doing exactly what a learner keeping one about a course is.
            */}
            <PersonalNotepad />
          </div>

          <div className="space-y-6">
            {/* Today before history: what is booked in the next few hours is
                actionable, and what changed yesterday is not. */}
            <DailySchedule schedule={schedule} />

            {/*
              ── ANNOUNCEMENTS, BETWEEN THE TWO ──────────────────────────────

              Between today's calendar and the hours chart on purpose: the
              schedule is what is happening now, an announcement is what
              somebody has been told, and the chart is history. The column
              then reads forward to back.

              `mb-0` because this column supplies its own gap — see the note
              on the component.
            */}
            <DashboardAnnouncements className="mb-0" />
            {/* Measured learning time across the organisation, by day or by
                week. The activity feed stood here and answered "what changed",
                which the Audit Log answers in full — this answers "is anybody
                actually learning", which nothing else on the page does at this
                resolution. */}
            <HoursActivity hours={trends.hours} />
          </div>
        </div>
      )}

    </>
  );
}

/**
 * Learning time across a chosen window.
 *
 * Opens on this month, day by day. A fixed twelve-month chart was the wrong
 * default for somebody checking how they are doing — eleven of its bars were
 * history and the one that could still move was a twelfth of the width.
 *
 * Bars are scaled to the tallest bucket rather than a fixed ceiling, so a
 * quiet month still reads as a shape rather than a flat line, and an empty
 * bucket keeps a visible stub — zero and "no data" look identical otherwise.
 */
function LearningActivity({ activity }: { activity: DashboardData['learner']['activity'] }) {
  /**
   * The applied window, as one piece of state.
   *
   * Preset and dates move together — picking "Last month" has to set both, and
   * holding them apart is how a label ends up describing a range it is not.
   */
  const [range, setRange] = useState<{ preset: RangePreset; from: string; to: string }>({
    preset: 'THIS_MONTH',
    from: activity.from,
    to: activity.to,
  });

  /**
   * The inline payload already IS this month, so the default costs no request.
   */
  const isInitial = range.from === activity.from && range.to === activity.to;

  const { data: fetched, isFetching } = useActivityRange(range.from, range.to, !isInitial);

  const shown = isInitial ? activity : (fetched ?? null);

  // Scaled in MINUTES, so twenty minutes is a visible bar rather than 0.3
  // rounded into nothing.
  const peak = Math.max(...(shown?.buckets ?? []).map((b) => b.minutes), 1);
  const delta = shown
    ? activityDelta(shown.totalMinutes, shown.previous.minutes, shown.changePercent)
    : null;
  const hasAny = (shown?.buckets ?? []).some((b) => b.minutes > 0);

  return (
    /* The id is the "This month" hero tile's destination — that tile counts
       the minutes this chart draws, and there is no other page for it. */
    <Card id="learning-activity" className="mt-6 scroll-mt-24 overflow-hidden">
      {/*
        Title left, picker hard right. `items-start` rather than centring, so
        the picker stays pinned to the top corner however the title wraps in a
        narrow column.
      */}
      <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold">Learning activity</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {shown
              ? `${shown.activeDays} active ${shown.activeDays === 1 ? 'day' : 'days'}`
              : 'Loading…'}
          </p>
        </div>

        <ActivityRangePicker
          preset={range.preset}
          from={range.from}
          to={range.to}
          onChange={setRange}
        />
      </div>

      <div className="px-5 py-4">
        {/*
          ── THE TOTAL, THEN THE COMPARISON, ON TWO LINES ────────────────────

          These three facts used to share one baseline-aligned row — the
          figure, a bare percentage, and "vs 0m last month" — so the headline
          and its footnote read at the same level and the eye had to sort out
          which was which.

          The amount is the fact and it goes first, with the period named
          beside it. Everything qualifying it drops to a second line, the same
          shape the Organisation hours activity card uses, so the two cards
          are read the same way.
        */}
        <div className="flex flex-wrap items-baseline gap-2">
          <p className="text-[26px] font-semibold leading-none tabular-nums">
            {shown ? formatMinutes(shown.totalMinutes) : '—'}
          </p>
          <span className="text-xs text-muted-foreground">{periodPhrase(range.preset)}</span>
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {/*
            A percentage where one can be trusted, the plain difference where
            it cannot — see `activityDelta`. The pill is never absent merely
            because the previous month was empty, which is most months on a
            new account and exactly when somebody wants to see movement.
          */}
          {delta && (
            <span
              className={cn(
                'inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5',
                'text-[11px] font-semibold tabular-nums',
                delta.direction === 'up'
                  ? 'bg-success/10 text-success'
                  : delta.direction === 'down'
                    ? 'bg-destructive/10 text-destructive'
                    : 'bg-muted text-muted-foreground',
              )}
            >
              {delta.direction === 'up' ? (
                <TrendingUp className="h-3 w-3 shrink-0" aria-hidden />
              ) : delta.direction === 'down' ? (
                <TrendingDown className="h-3 w-3 shrink-0" aria-hidden />
              ) : (
                <Minus className="h-3 w-3 shrink-0" aria-hidden />
              )}
              {delta.label}
            </span>
          )}

          {/*
            ── WHAT THE FIGURE IS BEING COMPARED AGAINST ──────────────────

            The percentage is null whenever the previous window was too small
            to divide by, so on the screen that prompted this the card showed
            "10m" and nothing to measure it against. The previous total is
            ALWAYS shown, zero included, because "ten minutes where you had
            done none" IS the comparison — and it is the half that still says
            something when the percentage cannot.

            It said "in 1–25 Aug" rather than "last month", because for a
            month-to-date chart the previous window used to be the equally
            long run of days ENDING the day before — the back end of August,
            which no one could name. That window is now the SAME DAYS of the
            month before, so the plain word is accurate and the dates moved to
            the tooltip where they remain checkable.

            The phrase follows the PRESET: picking "Last month" compares
            against the month before that, not against last month.
          */}
          {shown && (
            <span
              className="text-xs text-muted-foreground"
              title={`${formatMinutes(shown.previous.minutes)} in ${rangeLabel(
                shown.previous.from,
                shown.previous.to,
              )}`}
            >
              {/* Worded from the DIFFERENCE, not from the percentage — the
                  percentage is the thing that can be absent. */}
              {delta === null
                ? ''
                : delta.direction === 'down'
                  ? 'less than '
                  : delta.direction === 'up'
                    ? 'more than '
                    : 'same as '}
              <span className="font-medium tabular-nums text-foreground/70">
                {formatMinutes(shown.previous.minutes)}
              </span>{' '}
              {comparisonPhrase(range.preset)}
            </span>
          )}
        </div>

        {/* An empty chart says why it is empty. A row of flat stubs and a big
            zero look like a broken widget, and the honest reading — nothing
            has been recorded in this window — is the one thing it was not
            saying. */}
        {shown && !hasAny && (
          <p className="mt-1.5 text-xs text-muted-foreground">
            {activity.lastActiveOn
              ? 'No learning recorded in this period. Try a wider range.'
              : 'No learning recorded yet. Time is counted as you watch, read and sit assessments.'}
          </p>
        )}

        {/*
          A line, not bars.

          Bars compare discrete things to each other; this is one quantity
          moving through time. On a month with two active days, thirty-one bars
          of which twenty-nine were stubs read as a broken chart rather than a
          quiet month. `bestDayMinutes` went with them — the peak is now
          visible as the peak, so a figure restating it was one more number on
          a line that had four.
        */}
        <ActivityLineChart
          buckets={shown?.buckets ?? []}
          peak={peak}
          dimmed={isFetching}
        />
      </div>
    </Card>
  );
}

/**
 * The latest few announcements, tinted by priority.
 *
 * Its own query rather than another field on the dashboard payload — the
 * announcements feed is already fetched and cached by the Announcements page,
 * so arriving here from there costs nothing.
 */
function DashboardAnnouncements({
  /**
   * The gap below the card.
   *
   * `mb-6` by default, because the employee column stacks its panels with
   * their own margins. The admin column uses `space-y-6`, which supplies the
   * gap itself — so this is passed as `mb-0` there rather than doubling it to
   * forty-eight pixels, which is what a margin plus a gap quietly does.
   */
  className = 'mb-6',
}: { className?: string } = {}) {
  const [reading, setReading] = useState<AnnouncementItem | null>(null);

  /**
   * The shared hook, not a local `useQuery`.
   *
   * A second query on the same key returning a different shape is what put an
   * object where this component expected an array — see `announcementQueries`.
   */
  const { data } = useAnnouncementFeed();
  const items = data?.items;

  /**
   * One at a time, newest first, stepped through with the arrows.
   *
   * Three were stacked here and the panel ran most of the column. An
   * announcement is read once; the second and third were paying for permanent
   * space to say something already seen.
   */
  const [index, setIndex] = useState(0);

  if (!items || items.length === 0) return null;

  /**
   * Clamped rather than trusted.
   *
   * The feed refetches while this is open, and an announcement deleted
   * elsewhere shortens the list under a cursor that was already at the end —
   * which would index past it and blank the panel.
   */
  const at = Math.min(index, items.length - 1);
  const current = items[at]!;

  return (
    <>
      {/* Leads the side column now, so the gap belongs below it rather than above. */}
      <Card className={cn('overflow-hidden', className)}>
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-[15px] font-semibold">Announcements</h2>
          <Link
            to="/announcements"
            className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            All
          </Link>
        </div>

        <div className="p-4">
          {/* A one-item map, so the card below is untouched by the change from
              a list to a stepper — the same markup, shown once. */}
          {[current].map((item) => {
            /**
             * Where the link is decides what the heading says.
             *
             * A link in the BODY leaves the title alone — the title is a
             * heading and the body is the message. A link in the TITLE is
             * taken out of the heading, because the chip below already shows
             * it, and printing both gave the card the same destination twice:
             * an unreadable full URL as its heading and a tidy short one
             * underneath.
             *
             * A title that was nothing but the link leaves no heading at all,
             * and `heading` is empty — the chip becomes the announcement.
             */
            const bodyLink = firstLink(item.body);
            const titleLink = bodyLink ? null : firstLink(item.title);
            const link = bodyLink ?? titleLink;
            const heading = titleLink ? withoutLink(item.title, titleLink.value) : item.title;

            return (
            /*
              An overlay button, not a button wrapping the content.

              The row opens the announcement in place — these used to navigate
              away, which meant the only way to see an attached image was to
              leave the dashboard. But the body may contain a link, and an <a>
              inside a <button> is invalid markup that browsers handle however
              they like.

              So the click target is a sibling covering the card, and the
              content sits above it. Everything in the content ignores pointer
              events except the links, which therefore win the click while a
              press anywhere else still opens the dialog. Both remain real,
              focusable, keyboard-reachable elements.
            */
            <div
              key={item.id}
              className={cn(
                'relative rounded-xl border border-l-[3px] p-4 transition-opacity hover:opacity-90',
                /*
                  ── WHEN THE PICTURE IS THE ANNOUNCEMENT ───────────────────

                  A poster, a menu or a roster carries its own message, and
                  the body is now optional so people can post one without
                  padding it with a sentence describing the image beside it.
                  Shown as an 80px thumbnail that is unreadable — which is the
                  one thing it must not be.
                  
                  So with no body the card stacks and the image takes the full
                  width; with a body it stays a row, where the text is the
                  point and the thumbnail says one is attached.
                */
                item.body.trim() ? 'flex gap-3' : 'flex flex-col gap-3',
                // The shared rule, so the dashboard and the feed cannot drift
                // into two different reds. Normal and Low used to carry a grey
                // wash here, which put every card on a tint and left the
                // urgent ones with nothing to stand out against.
                priorityStyles(item.priority),
              )}
            >
              {/*
                `focus-visible`, and no ring offset.

                `focus-within` on the card fired whenever this button was
                CLICKED, not only when it was tabbed to — so an announcement
                somebody had opened kept a heavy outline afterwards. The offset
                made it worse by drawing a white gap between the card and the
                ring, which read as a second, misaligned box around the row.

                The ring sits on the overlay itself, inside the card's own
                bounds, so keyboard focus is still obvious and nothing is drawn
                outside the card.
              */}
              <button
                type="button"
                onClick={() => setReading(item)}
                className="absolute inset-0 z-0 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-700"
              >
                <span className="sr-only">Read “{item.title}”</span>
              </button>

              <span
                className={cn(
                  'pointer-events-none relative z-10 min-w-0',
                  item.body.trim() && 'flex-1',
                )}
              >
                {/*
                  ── THE HEADING DOES NOT REPEAT THE CHIP ────────────────────

                  When the link came from the TITLE, the raw URL is taken out
                  of the heading — otherwise the card printed the same
                  destination twice: the full `https://meet.google.com/…` as a
                  heading nobody can read, and the tidy short chip under it.

                  A title that was nothing but the URL leaves no heading at
                  all, and none is drawn: the chip is the announcement. A title
                  with words around the link keeps the words.

                  `heading` is computed with the link above so the two can
                  never disagree about which URL is being shown.
                */}
                {heading && (
                  <span className="block text-sm font-semibold leading-snug">{heading}</span>
                )}
                {/*
                  Plain text here, deliberately — the link is offered ONCE, as
                  the chip below.

                  Linkifying the body as well showed the same URL twice: a long
                  truncated one inside the sentence and a short tidy one under
                  it. Two controls for one destination is one too many, and the
                  chip is the better of the two — always visible whatever the
                  clamp cuts, and short enough to read.

                  No `block` alongside the clamp either: it works by setting
                  `display: -webkit-box`, and `block` fights it, which is why
                  these cards ran to full height instead of truncating.

                  Four lines rather than two, by request. Two cut most messages
                  mid-sentence, so the card showed an opening clause and the
                  reader had to open the dialog to learn whether it concerned
                  them. Four is roughly a short paragraph — enough to decide —
                  and the clamp still stops a long one taking over the panel.
                */}
                {/* Guarded: an empty body still rendered a span carrying its
                    own top margin, which left a gap under the title of an
                    image-only announcement. */}
                {item.body.trim() && (
                  <span className="mt-1.5 line-clamp-4 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                    {item.body}
                  </span>
                )}

                {/*
                  The link, lifted out of the message.

                  Four lines of a clamped card often do not reach the meeting
                  URL — it is conventionally the last thing in the message — so
                  linkifying the body alone would leave the one thing somebody
                  needs in a hidden line.

                  The TITLE is searched too, and second rather than first.
                  People paste a meeting link wherever it reads best, and a
                  notice whose title is just the URL with a poster underneath
                  is common enough that looking only at the body left it dead.
                  Body first because a message that has both is using the title
                  as a heading and the body as the content — the link in the
                  content is the one that was written to be followed.
                */}
                {link ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="pointer-events-auto relative mt-2 inline-flex max-w-full items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-xs font-medium text-brand-700 transition-colors hover:bg-accent"
                      title={link.href}
                    >
                    <LinkIcon className="h-3 w-3 shrink-0" aria-hidden />
                    <span className="truncate">{shortenUrl(link.href)}</span>
                  </a>
                ) : null}

                <span className="mt-2.5 block text-xs text-muted-foreground/80">
                  {item.createdBy.firstName} {item.createdBy.lastName}
                  {item.publishedAt && (
                    <>
                      {' · '}
                      {new Date(item.publishedAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </>
                  )}
                </span>
              </span>

              {/* No `onClick` — the overlay behind it already opens the row,
                  and a second button here would just be a smaller target for
                  the same action. */}
              {item.mediaUrl && (
                <AnnouncementThumb
                  url={item.mediaUrl}
                  title={item.title}
                  className={cn(
                    'pointer-events-none relative z-10',
                    item.body.trim()
                      ? /* Beside four lines of text. 20 rather than 14: at
                           56px an attached photo was a stamp, too small to
                           tell what it was of — which is the only reason to
                           put it on the card. 80px still leaves the body its
                           width. */
                        'h-20 w-20 shrink-0'
                      : /* The whole card. Capped in height so a tall poster
                           does not push the panel's other announcements off
                           the screen, and `w-full` so a wide one fills it. */
                        'h-44 w-full',
                  )}
                />
              )}
            </div>
            );
          })}
        </div>

        {/*
          The stepper, only when there is something to step to.

          Sideways rather than up and down: these are a sequence you page
          through, and left/right is the gesture for that everywhere else —
          vertical chevrons read as "expand", which is a different promise.

          Both directions, though only one was asked for: a single arrow has to
          wrap, and wrapping past the end to get back one is a worse answer
          than an arrow that is simply disabled at the edges. The position is
          stated because an arrow that stops without saying why reads as
          broken.
        */}
        {items.length > 1 && (
          <div className="flex items-center gap-2 border-t px-4 py-2.5">
            <span className="font-mono text-[11px] text-muted-foreground tabular-nums">
              {at + 1} of {items.length}
            </span>

            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => setIndex(Math.max(0, at - 1))}
                disabled={at === 0}
                aria-label="Previous announcement"
                title="Previous announcement"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => setIndex(Math.min(items.length - 1, at + 1))}
                disabled={at === items.length - 1}
                aria-label="Next announcement"
                title="Next announcement"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        )}
      </Card>

      <AnnouncementDialog
        announcement={reading}
        onClose={() => setReading(null)}
      />
    </>
  );
}

/**
 * Milestones as one horizontal strip.
 *
 * Replaces a two-column grid of description cards that ran the height of the
 * column for three badges. A badge is recognised by its icon and its name;
 * the description is what the achievements screen is for.
 *
 * Locked ones are shown, dimmed and dashed, because a milestone nobody can see
 * is not something to aim at — and they carry the "3 of 9" denominator.
 */
function AchievementStrip({
  badges,
  totalBadges,
}: {
  badges: DashboardData['learner']['badges'];
  totalBadges: number;
}) {
  const earned = badges.filter((b) => b.earnedAt).length;

  if (badges.length === 0) return null;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b px-5 py-3.5">
        <h2 className="text-[15px] font-semibold">Achievements</h2>
        <span className="font-mono text-[11px] text-muted-foreground">
          {earned} of {totalBadges}
        </span>
        <Link
          to="/achievements"
          className="ml-auto text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          All
        </Link>
      </div>

      {/*
        One row that scrolls sideways, and never cuts a badge in half.
        ──────────────────────────────────────────────────────────────────────
        The slicing was never really caused by the scrolling — it was caused by
        a FIXED badge width (104px) inside a column of arbitrary width. However
        many whole badges fitted, the remainder showed as a sliver of the next
        one, and no amount of scrolling changed that.

        So the badge width is derived from the container instead: exactly six
        fit on a wide screen, four on a tablet, three on a phone. `basis`
        subtracts the gaps first — five gaps between six badges — or the sixth
        would be pushed out by exactly the width of the gaps and reappear as
        the same sliver.

        `snap-x snap-mandatory` then keeps it honest while scrolling: every
        resting position lands on a badge's leading edge, so a flick that stops
        halfway is corrected rather than left mid-badge. `scroll-pl-4` matches
        the padding, or the snap would align badges to the card's border and
        tuck the first one under its own padding.
      */}
      <div className="flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-pl-4 p-4 scrollbar-thin">
        {badges.map((badge) => {
          const isEarned = Boolean(badge.earnedAt);
          return (
            <div
              key={badge.name}
              title={
                isEarned
                  ? `${badge.description} — earned ${new Date(badge.earnedAt!).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                  : badge.description
              }
              className={cn(
                /* Width derived from the container, never fixed — see the note
                   above. `shrink-0` so the flex row scrolls instead of
                   squeezing fifteen badges into one screen. */
                'flex shrink-0 snap-start flex-col items-center rounded-xl border px-2 py-3.5 text-center',
                /* Three, four, then six across. The subtracted figure is
                   always (badges − 1) × the 0.625rem gap, so the last one in
                   view ends flush with the edge instead of being pushed out by
                   the width of the gaps and returning as a sliver. */
                'basis-[calc((100%-1.25rem)/3)]',
                'sm:basis-[calc((100%-1.875rem)/4)]',
                'lg:basis-[calc((100%-3.125rem)/6)]',
                isEarned ? 'bg-card' : 'border-dashed bg-muted/25',
              )}
            >
              <span
                className={cn('text-[22px] leading-none', !isEarned && 'opacity-30 grayscale')}
                aria-hidden
              >
                {badge.icon}
              </span>
              <p
                className={cn(
                  'mt-2 line-clamp-2 text-[11.5px] font-medium leading-tight',
                  !isEarned && 'text-muted-foreground/70',
                )}
              >
                {badge.name}
              </p>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/**
 * The course's own picture, at row size.
 *
 * The dashboard has always received `thumbnailUrl` and drew a fixed book icon
 * over the top of it, so every row looked identical and the uploaded artwork
 * went nowhere.
 *
 * A course with no thumbnail falls back to the same derived colour and symbol
 * its card uses, rather than to a generic icon — so a course looks the same
 * everywhere it appears, which is the whole point of deriving it.
 */
function CourseTile({
  course,
  /** Smaller on a dense list, where the tile is a marker rather than a picture. */
  size = 'default',
}: {
  course: CourseSummary;
  size?: 'default' | 'sm';
}) {
  const [failed, setFailed] = useState(false);
  const art = courseArt(course.id, course.title);

  const box = size === 'sm' ? 'h-9 w-9 rounded-lg' : 'h-11 w-11 rounded-xl';

  if (course.thumbnailUrl && !failed) {
    return (
      <img
        src={course.thumbnailUrl}
        alt=""
        onError={() => setFailed(true)}
        loading="lazy"
        className={cn('shrink-0 border object-cover', box)}
      />
    );
  }

  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center leading-none',
        box,
        size === 'sm' ? 'text-[15px]' : 'text-[18px]',
      )}
      style={{ background: art.color }}
      aria-hidden
    >
      {art.symbol}
    </span>
  );
}

/**
 * What the person is here to do, by the authority they hold.
 *
 * Replaces a time-of-day greeting, which said nothing and was wrong for
 * anyone working a late shift.
 */
function tagline(permissions: string[]): string {
  if (permissions.includes('scope.organization')) return 'Everything is in your hands.';
  if (permissions.includes('scope.department')) return "Let's help your team grow.";
  return 'Learn. Grow. Move forward.';
}

/**
 * A destination, but only for somebody allowed to arrive at it.
 *
 * The routes these tiles point at are permission-guarded, and a guard that
 * bounces you back to the page you clicked from is a worse experience than a
 * figure that was never a link. Undefined means "render it plain".
 */
function linkIf(permissions: string[], permission: string, to: string): string | undefined {
  return permissions.includes(permission) ? to : undefined;
}

/**
 * One figure in the hero band — and, given somewhere to go, the way there.
 *
 * ── Why these are links ────────────────────────────────────────────────────
 * Every one of these numbers is a summary of a screen that already exists.
 * "58 CERTIFICATES" and the Certificates page are the same fact at two
 * resolutions, so the tile was asking a question it could not answer and
 * leaving the reader to find the menu entry themselves.
 *
 * `to` is optional and computed at the call site from the reader's
 * permissions, so a tile whose page they cannot open stays a plain figure
 * rather than a link into a redirect. That check belongs at the call site
 * because only the caller knows which permission guards which route.
 *
 * A `to` beginning with `#` scrolls within this page instead of navigating —
 * used by the learner's "This month" tile, whose detail view is the Learning
 * activity chart further down rather than a page of its own. It stays a real
 * anchor so it still works from the keyboard and without JavaScript.
 */
function HeroStat({
  icon: Icon,
  value,
  label,
  sub,
  detail,
  delta,
  deltaUnit = 'percent',
  to,
  reserveQualifier = false,
}: {
  icon: typeof BookOpen;
  value: string | number;
  label: string;
  /**
   * ONE short line saying what the figure counts.
   *
   * Optional, and an employee's tiles pass none — "5 COURSES DONE" about
   * yourself needs no footnote. An administrator's do: "3 ACTIVE" on an
   * organisation panel could be users, courses or learners.
   *
   * Kept to a few words on purpose. The first attempt put the whole
   * definition here — population, period and target — and five tiles of four
   * wrapped lines each read as a paragraph chopped into columns. The tile
   * answers "what is this"; `detail` answers "exactly".
   */
  sub?: string;
  /** The full sentence, on hover. Precision without the clutter. */
  detail?: string;
  /**
   * Movement against the previous window of the same length.
   *
   * ONLY for a figure that is a FLOW — something accumulated within a period,
   * like hours learned or people who showed up. Never for a stock: "courses
   * published" and "certificates issued all time" only ever go up, so a change
   * badge on either is always green and always says nothing.
   *
   * Null means there is no comparison to draw — usually because the previous
   * window was empty, where any percentage would be invented. The tile then
   * shows nothing at all: a note saying "none before" was tried and removed,
   * because on a young deployment every tile would carry one and a row of
   * them reads as an error rather than as an absence.
   */
  delta?: number | null;
  /**
   * What the delta IS — a percentage, or a plain difference in the same unit
   * as the figure above it.
   *
   * People are counted, so "+34" is the honest thing to say about them: it is
   * subtraction rather than a ratio, it needs no baseline to be safe, and it
   * is the number somebody would actually repeat. "+227%" was accurate and
   * told nobody that thirty-four more people had turned up.
   *
   * Hours stay a percentage. "+44h" against a hundred-odd is harder to place
   * than "+76%", and unlike a headcount the total is not a figure anybody
   * holds in their head.
   */
  deltaUnit?: 'percent' | 'count';
  /** Where the figure leads. A route, a `#id` on this page, or nothing. */
  to?: string;
  /**
   * Keep room for the sub-line even though THIS tile has nothing to put in
   * it, because a tile beside it does.
   *
   * Strip-level knowledge, so the strip passes it rather than the tile
   * guessing. The learner's four tiles carry nothing except a change badge on
   * "This month", and that badge is absent whenever last month was too thin
   * to compare against — so on most accounts no tile has a qualifier at all,
   * and reserving the row unconditionally left every strip an empty line
   * taller than it needed to be.
   */
  reserveQualifier?: boolean;
}) {
  /*
    No min-width: the tile is a grid cell, and a floor on it would push the
    row past its container before the columns could even out.

    The top and left borders are this tile's contribution to the strip's grid
    lines — the container pulls the grid up and left by a pixel so the
    outermost of them are clipped. See the note at the learner strip for why
    the lines are drawn here rather than with `divide-*` on the container.

    Left-aligned, on both strips. Centring was tried and reverted: the five
    administrator tiles carry a sub-line and a change pill under the label,
    and centring three stacked lines of different lengths made each tile read
    as its own little poster instead of a column in a row. Ranged left, the
    figure, the label and the sub-line all start on one edge and the eye runs
    down them.

    `flex flex-col justify-center` rather than `block`: grid cells stretch to
    a common height, so if one tile in a strip is ever shorter than its
    neighbours its contents stay centred against them instead of riding up.
  */
  const base =
    'relative flex flex-col justify-center border-l border-t border-brand-200/60 px-3.5 py-3 text-left dark:border-white/10';

  /** Something of this tile's own to show beneath the label. */
  const hasQualifier = Boolean(sub) || (delta !== null && delta !== undefined);


  const body = (
    <>
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 shrink-0 text-brand-600" strokeWidth={1.75} aria-hidden />
        {/* Mono, so the figures share a digit width and the labels beneath
            them line up in a column rather than drifting. */}
        <p className="font-mono text-[19px] font-semibold leading-none">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
      </div>
      {/*
        ── ONE LINE, AND IT HAS TO FIT ───────────────────────────────────────

        This label has now been wrong in both directions. `truncate` clipped
        "ACTIVE LEARNERS" to "ACTIVE LEARNE…"; letting it wrap fixed the
        clipping and cost the tile a line, which put this row out of step with
        itself the moment one label was longer than the others.

        The real problem was never the wrapping — it was the TRACKING. `2xs`
        carries `0.08em`, which on a fifteen-character uppercase label spends
        about twelve pixels on letter-spacing alone, in a tile roughly 125px
        wide. Tightening it to `0.03em` buys back the width the label needed,
        and `whitespace-nowrap` says plainly that one line is the contract.

        `truncate` stays as the backstop for a label nobody has thought about
        yet — but it should now never fire on the five that exist.
      */}
      <p className="mt-1.5 truncate whitespace-nowrap text-2xs font-medium uppercase tracking-[0.03em] text-muted-foreground">
        {label}
      </p>

      {/*
        ── THE SUB-LINE AND THE DELTA SHARE A ROW ────────────────────────────

        The delta used to be a row of its own, so a tile grew taller the
        moment it had a comparison to show — the row's five tiles were then
        two different heights depending on which figures happened to be
        flows. "+226.67% vs previous" also wrapped inside its own row, which
        made it two lines rather than one.

        Both facts are small and both qualify the figure above them, so they
        sit together: "this month  +227%". No tile can grow by acquiring a
        delta, because there is no row to acquire.

        `min-h` reserves this line on EVERY tile, including the employee
        dashboard's, which passes neither — so a tile with nothing to say
        here is still the same height as one with both.
      */}
      {/*
        `items-center`, not `items-baseline`: the delta is a pill with a glyph
        in it, and a flex box's baseline is its content's, so baseline
        alignment hung it a pixel or two low against the text. `min-h` is the
        pill's own height.

        ── RESERVED WHEN THE STRIP NEEDS IT, NOT ALWAYS ─────────────────────

        Two failures sit either side of this, and the rule has been wrong in
        both directions already.

        Reserve it never, and a strip where ONE tile has a change badge draws
        its three neighbours a line shorter, so four figures that should read
        across as a row sit at two different heights.

        Reserve it always, and a strip where NO tile has anything — which is
        most learner dashboards, since the badge needs a comparable previous
        month — carries an empty line under every figure and the card is
        visibly taller than its contents.

        So the tile draws the row when it has something of its own, and the
        strip says when a neighbour does.
      */}
      {(hasQualifier || reserveQualifier) && (
      <div className="mt-0.5 flex min-h-[1rem] items-center gap-1.5">
        {sub && <p className="min-w-0 truncate text-[10px] text-muted-foreground/70">{sub}</p>}

        {delta !== null && delta !== undefined && (
          <span
            /* Whole numbers. `round2` sends 226.67, and two decimal places on
               a change of two hundred per cent is precision about noise. */
            title={
              deltaUnit === 'count'
                ? 'More than in the same stretch of the previous month'
                : 'Against the same stretch of the previous month'
            }
            className={cn(
              /*
                ── A PILL, NOT A LOOSE FIGURE ─────────────────────────────

                As plain coloured text this sat at the same weight as the
                sub-line beside it, so "+34" read as part of the sentence
                "this month" rather than as a separate fact about direction.

                A tinted pill with an arrow makes it one object you either
                read or skip — and the arrow is what carries the meaning for
                anybody who cannot separate the green from the red, which
                colour alone never does.

                `py-px` and a 9px glyph keep it inside the line height the
                row already reserves, so acquiring a delta still cannot make
                a tile taller than the ones beside it.
              */
              'inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-px',
              'text-[10px] font-semibold tabular-nums',
              /* Zero is neither good nor bad, and colouring it green would
                 read as progress where nothing moved. */
              delta > 0
                ? 'bg-success/10 text-success'
                : delta < 0
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-muted text-muted-foreground',
            )}
          >
            {delta > 0 ? (
              <TrendingUp className="h-2.5 w-2.5 shrink-0" aria-hidden />
            ) : delta < 0 ? (
              <TrendingDown className="h-2.5 w-2.5 shrink-0" aria-hidden />
            ) : (
              <Minus className="h-2.5 w-2.5 shrink-0" aria-hidden />
            )}
            {delta > 0 ? '+' : ''}{Math.round(delta)}{deltaUnit === 'percent' ? '%' : ''}
          </span>
        )}
      </div>
      )}

      {/*
        The only thing that marks a tile as clickable, and it appears on hover
        and on focus. A permanent arrow on five tiles would read as decoration
        within a minute; absent entirely, nobody would know to click. It is
        positioned rather than laid out so it cannot change the tile's height
        and put this row out of step with the one on the other dashboard.
      */}
      {to && (
        <ArrowUpRight
          className="absolute right-2 top-2 h-3 w-3 text-brand-600 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          aria-hidden
        />
      )}
    </>
  );

  if (!to) {
    return (
      <div className={base} title={detail}>
        {body}
      </div>
    );
  }

  /**
   * `hover:bg-white` reads against the band's `bg-white/60`, so the tile under
   * the cursor lifts out of the row rather than merely tinting.
   */
  const interactive =
    'group transition-colors hover:bg-white focus-visible:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ' +
  /* The dark equivalent of "one step brighter than the strip": white at a low
     alpha lifts off any dark ground, where a fixed tint would have to be
     re-picked every time the ground under it changed. */
  'dark:hover:bg-white/[0.08] dark:focus-visible:bg-white/[0.08] dark:focus-visible:ring-brand-400';

  if (to.startsWith('#')) {
    return (
      <a
        href={to}
        className={cn(base, interactive)}
        title={detail}
        onClick={(event) => {
          const target = document.querySelector(to);
          if (!target) return; // Let the browser's own jump handle it.
          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      >
        {body}
      </a>
    );
  }

  return (
    <Link to={to} className={cn(base, interactive)} title={detail}>
      {body}
    </Link>
  );
}

/**
 * "3h 44m", "45m", "0m".
 *
 * Not whole hours. Rounding turned 3h 44m into "4h", and — the part that
 * actually loses trust — anybody with twenty minutes of genuine learning into
 * "0h". Somebody who cannot see their own effort reflected stops believing the
 * figure, which is exactly what prompted this.
 *
 * Minutes are dropped once past ten hours, where they stop being interesting
 * and start being noise.
 */
function formatLearningTime(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (hours >= 10 || rest === 0) return `${hours}h`;
  return `${hours}h ${rest}m`;
}

/** "due in 4 days" / "due tomorrow" / "due today". */
function dueInWords(days: number): string {
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  return `due in ${days} days`;
}

/** "1h 20m left", from the author's duration estimate and progress so far. */
function remainingLabel(durationMinutes: number, percent: number): string {
  const left = Math.max(0, Math.round(durationMinutes * (1 - percent / 100)));
  if (left === 0) return 'almost done';
  const hours = Math.floor(left / 60);
  const minutes = left % 60;
  return hours > 0 ? `${hours}h ${minutes}m left` : `${minutes}m left`;
}

/**
 * "13–31 Aug" / "28 Jul – 3 Aug" — a `YYYY-MM-DD` pair, said the short way.
 *
 * Parsed with an explicit `T00:00:00` so the string is read as a LOCAL day.
 * `new Date('2026-08-13')` is UTC midnight, which in any timezone west of
 * Greenwich is the 12th — the same one-day slip this codebase has now fixed in
 * three places.
 */
function rangeLabel(from: string, to: string): string {
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  const month = (d: Date) => d.toLocaleDateString('en-IN', { month: 'short' });
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();

  if (from === to) return `${start.getDate()} ${month(start)}`;

  if (sameMonth) {
    /**
     * A window covering the whole month is NAMED, not spelled out.
     *
     * The comparison is "September against August" now — see `previousWindow`
     * on the API — and "1–31 Aug" is a laboured way to write August. The
     * part-month form stays for a month-to-date window, because "1–24 Aug" and
     * "August" are genuinely different claims and the shorter one would
     * overstate what is being compared.
     */
    const lastOfMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
    if (start.getDate() === 1 && end.getDate() === lastOfMonth) {
      return end.toLocaleDateString('en-IN', { month: 'long' });
    }

    // The month is said once when both ends share it, which is the common case.
    return `${start.getDate()}–${end.getDate()} ${month(end)}`;
  }

  return `${start.getDate()} ${month(start)} – ${end.getDate()} ${month(end)}`;
}

function shortDate(value: string): string {
  return new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// `formatDue` lived here for the old deadlines list. The Upcoming card now
// carries its own label per entry — "Deadline", "3:00pm · Zoom" — because a
// calendar event is not a due date and should not be described as one.
